import { getBackendUrl } from '../utils/apiUrl';

const BACKEND_URL = getBackendUrl();

/** Complete provider-safe posting; never fall back to a match-feed summary. */
export const getListingReviewDetail = async (listingId) => {
    if (!listingId) return null;
    try {
        const res = await fetch(`${BACKEND_URL}/normalized-listings/detail/${encodeURIComponent(listingId)}`);
        if (res.status === 404 || res.status === 410) {
            throw Object.assign(new Error('This job is no longer available.'), { code: 'LISTING_UNAVAILABLE' });
        }
        if (!res.ok) return null;
        return await res.json();
    } catch (error) {
        if (error.code === 'LISTING_UNAVAILABLE') throw error;
        return null;
    }
};

/** Fetch a single listing by ID */
export const getListingDetail = async (token, listingId) => {
    try {
        // Try the listings endpoint first (may 403 for providers)
        let res = await fetch(`${BACKEND_URL}/listings/${listingId}`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (res.ok) {
            const data = await res.json();
            if (data) return data;
        }

        // Try normalized-listings (public, no auth needed)
        res = await fetch(`${BACKEND_URL}/normalized-listings/${listingId}`);
        if (res.ok) {
            const data = await res.json();
            if (data) return data;
        }

        // Fallback: fetch from provider-matches and extract the embedded listing
        if (token) {
            res = await fetch(`${BACKEND_URL}/matching/provider-matches`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) {
                const matches = await res.json();
                if (Array.isArray(matches)) {
                    for (const match of matches) {
                        const listing = match.listing || match.listingId || {};
                        const lid = listing._id || listing.id;
                        if (lid && lid.toString() === listingId.toString()) {
                            return {
                                ...listing,
                                matchScore: match.matchScore || 0,
                            };
                        }
                    }
                }
            }
        }
    } catch (err) {
        console.error('[getListingDetail] Error:', err);
    }

    // No listing found — return null for the page to show an empty/error state
    return null;
};

/** Fetch similar listings */
export const getSimilarListings = async (token, listingId) => {
    try {
        const res = await fetch(`${BACKEND_URL}/listings/${listingId}/similar`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) return data.slice(0, 5);
        }
    } catch { /* fall through */ }

    // If no similar endpoint, pull from matches as related opportunities
    if (token) {
        try {
            const res = await fetch(`${BACKEND_URL}/matching/provider-matches?limit=5`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (res.ok) {
                const matches = await res.json();
                if (Array.isArray(matches)) {
                    return matches
                        .filter(m => {
                            const lid = (m.listing || m.listingId)?._id;
                            return lid && lid.toString() !== listingId.toString();
                        })
                        .slice(0, 3)
                        .map(m => {
                            const listing = m.listing || m.listingId || {};
                            return { ...listing, matchScore: m.matchScore || 0 };
                        });
                }
            }
        } catch { /* fall through */ }
    }

    return [];
};

/** Express interest in a listing */
export const expressInterest = async (token, listingId) => {
    try {
        const res = await fetch(`${BACKEND_URL}/matching/swipe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ listingId, direction: 'right', fitAcknowledged: true }),
        });
        return res.ok;
    } catch {
        return false;
    }
};

/** Pass on a listing */
export const dismissListing = async (token, listingId) => {
    try {
        const res = await fetch(`${BACKEND_URL}/matching/swipe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ listingId, direction: 'left' }),
        });
        return res.ok;
    } catch {
        return false;
    }
};

/** Check swipe status for a listing */
export const getSwipeStatus = async (token, listingId) => {
    try {
        const res = await fetch(`${BACKEND_URL}/matching/swipe?listingId=${listingId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return 'none';
        const data = await res.json();
        // May return array of swipes — check direction of most recent
        if (Array.isArray(data) && data.length > 0) {
            return data[0].direction === 'right' ? 'interested' : 'passed';
        }
        return data.direction === 'right' ? 'interested' : (data.direction === 'left' ? 'passed' : 'none');
    } catch {
        return 'none';
    }
};

/** Cross-reference listing requirements with provider credentials */
export const checkCredentialReadiness = async (token, requirements = []) => {
    try {
        const res = await fetch(`${BACKEND_URL}/credentialing/vault/summary/stats`, {
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return { items: [], allMet: false };
        const creds = await res.json();
        const credMap = {};
        (Array.isArray(creds) ? creds : creds.credentials || []).forEach(c => {
            credMap[(c.name || c.type || '').toLowerCase()] = c;
        });

        const items = requirements.map(req => {
            const match = credMap[req.toLowerCase()];
            if (!match) return { name: req, status: 'missing' };
            if (match.expiryDate && new Date(match.expiryDate) < new Date()) return { name: req, status: 'expired', detail: `Expired ${match.expiryDate}` };
            if (match.status === 'verified' || match.documentUrl) return { name: req, status: 'met', detail: match.expiryDate ? `Expires ${match.expiryDate}` : 'Current' };
            return { name: req, status: 'pending' };
        });

        return { items, allMet: items.every(i => i.status === 'met') };
    } catch {
        return { items: [], allMet: false };
    }
};
